import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Home, ShoppingBag, Wallet, User, ShoppingCart, Bell } from "lucide-react";
import { Logo } from "@/components/Logo";
import { ThemeToggle, LanguageToggle } from "@/components/Toggles";
import { useLang } from "@/contexts/LanguageContext";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { AiAssistant } from "@/components/AiAssistant";

export const CustomerLayout = ({ children }) => {
  const { t } = useLang();
  const { count } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex flex-col" data-testid="customer-layout">
      <header className="sticky top-0 z-40 bg-[var(--so-surface)] border-b border-[var(--so-border)]">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/app"><Logo size={30} /></Link>
          <div className="flex items-center gap-2">
            <LanguageToggle />
            <ThemeToggle />
            <button
              onClick={() => navigate("/app/notifications")}
              data-testid="notifications-btn"
              className="w-10 h-10 inline-flex items-center justify-center rounded-full border border-[var(--so-border)] hover:border-[var(--so-orange)] transition-colors"
            >
              <Bell className="w-4 h-4" />
            </button>
            <button
              onClick={() => navigate("/app/cart")}
              data-testid="cart-btn"
              className="relative w-10 h-10 inline-flex items-center justify-center rounded-full border border-[var(--so-border)] hover:border-[var(--so-orange)] transition-colors"
            >
              <ShoppingCart className="w-4 h-4" />
              {count > 0 && (
                <span className="absolute -top-1 -right-1 bg-[var(--so-orange)] text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center" data-testid="cart-badge">
                  {count}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 pb-24">{children}</main>

      <nav className="fixed bottom-0 inset-x-0 z-40 bg-[var(--so-surface)] border-t border-[var(--so-border)]" data-testid="customer-bottom-nav">
        <div className="max-w-6xl mx-auto px-4 py-2 grid grid-cols-4">
          {[
            { to: "/app", icon: Home, label: t("home"), end: true, tid: "nav-home" },
            { to: "/app/orders", icon: ShoppingBag, label: t("orders"), tid: "nav-orders" },
            { to: "/app/wallet", icon: Wallet, label: t("wallet"), tid: "nav-wallet" },
            { to: "/app/profile", icon: User, label: t("profile"), tid: "nav-profile" },
          ].map(({ to, icon: Icon, label, end, tid }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={tid}
              className={({ isActive }) =>
                `flex flex-col items-center gap-1 py-1.5 transition-colors ${isActive ? "text-[var(--so-orange)]" : "text-[var(--so-text-soft)]"}`
              }
            >
              <Icon className="w-5 h-5" />
              <span className="text-[11px] font-medium">{label}</span>
            </NavLink>
          ))}
        </div>
      </nav>

      <AiAssistant />
    </div>
  );
};
